import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiTrash2, FiSearch, FiPrinter } from 'react-icons/fi';
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';

const StockList = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { grampanchayatName, arthikVarshLabel } = location.state || {};

    const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
    const [searchTerm, setSearchTerm] = useState('');
    const [stockItems, setStockItems] = useState([]);

    // Form State
    const initialFormState = {
        type: 'डेड स्टॉक',
        objectNo: '',
        name: '',
        description: '',
        rate: '',
        measurementType: 'नग',
        hsn: '0',
        taxRate: '0',
        permissions: [
            { id: 1, name: 'पाणीपुरवठा', allowed: false, initialCount: 0, initialAmount: 0 }
        ]
    };
    const [formData, setFormData] = useState(initialFormState);

    // Fetch Stock Data
    useEffect(() => {
        const stockRef = ref(database, 'Grampanchayat/Panipuravtha/Stock');
        onValue(stockRef, (snapshot) => {
            const data = snapshot.val();
            const loadedItems = [];
            if (data) {
                Object.keys(data).forEach((key) => {
                    loadedItems.push({
                        id: key,
                        ...data[key]
                    });
                });
            }
            // Sort by objectNo if available, otherwise by name
            loadedItems.sort((a, b) => (a.objectNo || 0) - (b.objectNo || 0));
            setStockItems(loadedItems);
        });
    }, []);

    // Set next Object No automatically when adding new
    useEffect(() => {
        if (viewMode === 'form' && !formData.id) {
            const nextNo = stockItems.length > 0 
                ? Math.max(...stockItems.map(item => parseInt(item.objectNo || 0))) + 1 
                : 1;
            setFormData(prev => ({ ...prev, objectNo: nextNo }));
        }
    }, [viewMode, stockItems]);

    const handlePermissionChange = (index, field, value) => {
        const newPermissions = [...formData.permissions];
        newPermissions[index][field] = value;
        setFormData({ ...formData, permissions: newPermissions });
    };

    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleEdit = (item) => {
        setFormData(item);
        setViewMode('form');
    };

    const handleSave = () => {
        if (!formData.name) {
            alert('कृपया नाव भरा');
            return;
        }

        const stockRef = ref(database, 'Grampanchayat/Panipuravtha/Stock');
        
        if (formData.id) {
            // Update existing
            const itemRef = ref(database, `Grampanchayat/Panipuravtha/Stock/${formData.id}`);
            update(itemRef, {
                ...formData,
                updatedAt: new Date().toISOString()
            })
            .then(() => {
                alert('माहिती यशस्वीरित्या अद्यतनित (Update) झाली');
                setFormData(initialFormState);
                setViewMode('list');
            })
            .catch((error) => {
                console.error("Error updating data: ", error);
                alert('माहिती अद्यतनित करताना त्रुटी आली');
            });
        } else {
            // Create new
            const newItem = {
                ...formData,
                createdAt: new Date().toISOString()
            };
            push(stockRef, newItem)
                .then(() => {
                    alert('माहिती यशस्वीरित्या साठवली');
                    setFormData(initialFormState);
                    setViewMode('list');
                })
                .catch((error) => {
                    console.error("Error saving data: ", error);
                    alert('माहिती साठवताना त्रुटी आली');
                });
        }
    };

    const handleDelete = (id) => {
        if(window.confirm('तुम्ही नक्की हि माहिती खोडू इच्छिता?')) {
            remove(ref(database, `Grampanchayat/Panipuravtha/Stock/${id}`));
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-poppins">
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-6">
                <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 flex flex-col md:flex-row justify-between items-center bg-gradient-to-r from-blue-700 to-blue-900 text-white">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => viewMode === 'form' ? setViewMode('list') : navigate(-1)} 
                            className="hover:bg-white/10 p-2 rounded-full transition-colors flex items-center justify-center"
                        >
                            <FiArrowLeft className="text-2xl" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold mb-1">
                                {viewMode === 'list' ? 'वस्तु / मालाची यादी' : 'वस्तु / मालाची माहिती'}
                            </h1>
                            <p className="text-blue-200 text-sm">
                                {grampanchayatName ? `${grampanchayatName} | ` : ''}आर्थिक वर्ष: {arthikVarshLabel || '२०२४-२५'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {viewMode === 'list' ? (
                // LIST VIEW
                <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="relative w-full md:w-1/2">
                            <input 
                                type="text" 
                                placeholder="शोधणे..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <FiSearch className="absolute left-3 top-3 text-gray-400" />
                        </div>
                        <button 
                            onClick={() => {
                                setFormData(initialFormState);
                                setViewMode('form');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold shadow-sm transition-colors"
                        >
                            नवीन
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-blue-800 text-white uppercase font-medium">
                                <tr>
                                    <th className="px-6 py-3 border-r border-blue-700">वस्तु क्र.</th>
                                    <th className="px-6 py-3 border-r border-blue-700 w-1/3">नाव</th>
                                    <th className="px-6 py-3 border-r border-blue-700">प्रकार</th>
                                    <th className="px-6 py-3 border-r border-blue-700">HSN नं.</th>
                                    <th className="px-6 py-3 border-r border-blue-700">कर दर</th>
                                    <th className="px-6 py-3 text-center">पर्याय</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {stockItems.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase())).map((item) => (
                                    <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-gray-900 border-r">{item.objectNo}</td>
                                        <td className="px-6 py-4 border-r">{item.name}</td>
                                        <td className="px-6 py-4 border-r">{item.type}</td>
                                        <td className="px-6 py-4 border-r">{item.hsn}</td>
                                        <td className="px-6 py-4 border-r">{item.taxRate}</td>
                                        <td className="px-6 py-4 text-center flex justify-center gap-3">
                                            <button 
                                                onClick={() => handleEdit(item)}
                                                className="bg-blue-700 text-white px-3 py-1 rounded hover:bg-blue-800 transition-colors text-xs font-bold"
                                            >
                                                बदल
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(item.id)}
                                                className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors text-xs font-bold"
                                            >
                                                खोडणे
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {stockItems.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4 text-gray-500">माहिती उपलब्ध नाही</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                // FORM VIEW
                <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md border border-gray-200 p-6 md:p-8">
                    <div className="grid grid-cols-1 gap-6">
                        {/* Type Selection */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">प्रकार:</label>
                            <select 
                                value={formData.type}
                                onChange={(e) => handleInputChange('type', e.target.value)}
                                className="md:col-span-3 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="डेड स्टॉक">डेड स्टॉक</option>
                                <option value="स्टॉक">स्टॉक</option>
                                <option value="टी.सी.एल.">टी.सी.एल.</option>
                            </select>
                        </div>

                        {/* Object No */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">वस्तु क्र.:</label>
                            <input 
                                type="text" 
                                value={formData.objectNo}
                                readOnly
                                className="md:col-span-1 w-full p-2 border border-blue-300 rounded bg-gray-100 cursor-not-allowed"
                                placeholder="Auto"
                            />
                        </div>

                        {/* Name */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">नाव:</label>
                            <input 
                                type="text" 
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="नाव भरा"
                                className="md:col-span-3 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Description */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-start gap-4">
                            <label className="text-gray-700 font-medium md:text-right mt-2">वर्णन:</label>
                            <textarea 
                                rows="3"
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder="वर्णन भरा"
                                className="md:col-span-3 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Rate */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">दर रक्कम:</label>
                            <input 
                                type="number" 
                                value={formData.rate}
                                onChange={(e) => handleInputChange('rate', e.target.value)}
                                className="md:col-span-3 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Measurement Type */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">मोजमाप प्रकार:</label>
                            <select 
                                value={formData.measurementType}
                                onChange={(e) => handleInputChange('measurementType', e.target.value)}
                                className="md:col-span-3 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="नग">नग</option>
                                <option value="किलोग्राम">किलोग्राम</option>
                                <option value="लिटर">लिटर</option>
                                <option value="मीटर">मीटर</option>
                            </select>
                        </div>

                        {/* HSN No */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">HSN No:</label>
                            <select 
                                value={formData.hsn}
                                onChange={(e) => handleInputChange('hsn', e.target.value)}
                                className="md:col-span-3 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="0">0</option>
                            </select>
                        </div>

                        {/* Tax Rate Display */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                           <div className="md:col-start-2 md:col-span-3 flex justify-between text-sm text-gray-600">
                                <span>कर दर 0%</span>
                                <span>सविस्तर माहिती -</span>
                           </div>
                        </div>

                        {/* Department Permissions Table */}
                        <div className="mt-4 border border-blue-800 rounded overflow-hidden">
                             <div className="bg-blue-800 text-white px-4 py-2 font-medium">
                                 विभाग परवानगी माहिती
                             </div>
                             <div className="overflow-x-auto">
                                <table className="w-full text-sm text-center">
                                    <thead className="bg-blue-700 text-white">
                                        <tr>
                                            <th className="p-2 border-r border-blue-600">परवानगी</th>
                                            <th className="p-2 border-r border-blue-600 w-1/3">विभागाचे नाव</th>
                                            <th className="p-2 border-r border-blue-600">आरंभीची संख्या</th>
                                            <th className="p-2">आरंभीची रक्कम</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                        {formData.permissions.map((perm, idx) => (
                                            <tr key={perm.id} className="border-b">
                                                <td className="p-2 border-r">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={perm.allowed}
                                                        onChange={(e) => handlePermissionChange(idx, 'allowed', e.target.checked)}
                                                        className="w-5 h-5 text-blue-600 rounded"
                                                    />
                                                </td>
                                                <td className="p-2 border-r text-left px-4">{perm.name}</td>
                                                <td className="p-2 border-r">
                                                    <input 
                                                        type="number" 
                                                        value={perm.initialCount}
                                                        onChange={(e) => handlePermissionChange(idx, 'initialCount', e.target.value)}
                                                        className="w-full p-1 border rounded text-right"
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input 
                                                        type="number" 
                                                        value={perm.initialAmount}
                                                        onChange={(e) => handlePermissionChange(idx, 'initialAmount', e.target.value)}
                                                        className="w-full p-1 border rounded text-right"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                             </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-center gap-4 mt-8">
                            <button 
                                onClick={handleSave}
                                className="bg-green-600 hover:bg-green-700 text-white px-8 py-2 rounded-lg font-bold shadow transition-colors"
                            >
                                साठविणे
                            </button>
                            <button 
                                onClick={() => setViewMode('list')}
                                className="bg-red-500 hover:bg-red-600 text-white px-8 py-2 rounded-lg font-bold shadow transition-colors"
                            >
                                रद्द करणे
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockList;
